/** @format */
import qs from 'qs';
import { useEffect } from 'react';

import { ModelSearchResultsType } from '@toucan/common/services/search/constants';
import { Query } from '@toucan/common/services/search/types';

import { useSortByOption } from '@toucan/client/features/search/components/model-search-results/components/sort/hooks/use-sort-by-option';
import { SearchResultsVariant } from '@toucan/client/features/search/components/search-results';
import { addSearchResultsToDataLayer } from '@toucan/client/features/search/helpers/analytics';

import { useSearchParams } from '../use-search-params';
import { useModelInfiniteResults } from './hooks/use-model-infinite';
import { useModelPaginatedResults } from './hooks/use-model-paginated';

const nullSearchResults = {
    results: [],
    resultCount: 0,
    totalResults: -1,
    spellcheck: {
        suggestions: [],
    },
    sort: {},
};

export const useModelSearchResults = (
    baseQuery: Partial<Query>,
    isInfiniteScroll: boolean,
    resultsPerPage: number,
    type: ModelSearchResultsType
) => {
    const searchParams = useSearchParams();

    const sortByOption = useSortByOption(type);

    const queryKey = qs.stringify(
        { baseQuery, ...searchParams },
        { arrayFormat: 'comma' }
    );

    const {
        data: searchResults,
        isLoading: isSearchResultsLoading,
        dataUpdatedAt: searchResultsUpdatedAt,
    } = useModelPaginatedResults(
        queryKey,
        isInfiniteScroll,
        resultsPerPage,
        baseQuery,
        searchParams,
        type
    );

    const {
        data: infiniteSearchResults,
        isLoading: isInfiniteSearchResultsLoading,
        isFetchingNextPage: isFetchingNextInfiniteSearchResults,
        dataUpdatedAt: infiniteSearchResultsUpdatedAt,
        fetchNextPage,
        hasNextPage,
    } = useModelInfiniteResults(
        queryKey,
        isInfiniteScroll,
        resultsPerPage,
        baseQuery,
        searchParams,
        type
    );

    useEffect(() => {
        if (!searchResults) return;

        addSearchResultsToDataLayer({
            searchResults: searchResults?.results ?? [],
            pageType: type,
            searchFilters: searchParams.selectedFilters ?? {},
            searchVariant: SearchResultsVariant.MODEL,
            sortedBy: sortByOption.value,
        });
    }, [searchResultsUpdatedAt, infiniteSearchResultsUpdatedAt]);

    if (isInfiniteScroll) {
        return {
            results: infiniteSearchResults ?? nullSearchResults,
            isLoading: isInfiniteSearchResultsLoading,
            isFetchingNextPage: isFetchingNextInfiniteSearchResults,
            fetchNextPage,
            hasNextPage,
        };
    }

    return {
        results: searchResults ?? nullSearchResults,
        isLoading: isSearchResultsLoading,
        isFetchingNextPage: false,
        hasNextPage: false,
    };
};
